import type { PaceCode } from '../constants/languages.ts';
import type { AIProviderCode } from '../constants/ai-providers.ts';
import { DEFAULT_AI_PROVIDER, DEFAULT_AI_MODEL } from '../constants/ai-providers.ts';
import { getModelPricing } from '../constants/ai-pricing.ts';
import { getBlockCount } from './composition.ts';

// =============================================================================
// Generation cost estimation
// =============================================================================
// Token estimates per exercise block call (content generation):
//   ~2,500 input tokens (system prompt + block-specific prompt)
//   ~2,000 output tokens (generated exercise content)
//
// Back matter (verb glossary + cultural notes): 2 extra calls
//   ~2,000 input tokens each
//   ~1,500 output tokens each
//
// HTML rendering (per block, via Haiku):
//   ~1,500 input tokens (system prompt + block JSON)
//   ~2,500 output tokens (rendered HTML)
//   ~1.15x retry multiplier (occasional validation retries)
// =============================================================================

const TOKENS_PER_BLOCK_INPUT = 2_500;
const TOKENS_PER_BLOCK_OUTPUT = 2_000;

const BACK_MATTER_CALLS = 2;
const TOKENS_PER_BACK_MATTER_INPUT = 2_000;
const TOKENS_PER_BACK_MATTER_OUTPUT = 1_500;

// HTML rendering estimates (per block, using Haiku)
const RENDER_TOKENS_PER_BLOCK_INPUT = 1_500;
const RENDER_TOKENS_PER_BLOCK_OUTPUT = 2_500;

/** Multiplier to account for occasional validation retries (up to 2 retries per block). */
const RENDER_RETRY_MULTIPLIER = 1.15;

/** Default model used by the render-html edge function. */
export const DEFAULT_RENDER_MODEL = 'claude-haiku-4-5-20251001';

export interface GenerationCostEstimate {
  /** Number of exercise blocks to generate. */
  blockCount: number;
  /** Estimated total page count for this pace tier. */
  estimatedPages: number;
  /** Estimated total input tokens across all content generation API calls. */
  estimatedInputTokens: number;
  /** Estimated total output tokens across all content generation API calls. */
  estimatedOutputTokens: number;
  /** Estimated content generation cost in USD. */
  estimatedCostUsd: number;
  /** AI provider used for content generation. */
  provider: AIProviderCode;
  /** Model ID used for content generation. */
  model: string;
  /** Estimated HTML rendering cost in USD (Haiku). */
  renderEstimatedCostUsd: number;
  /** Model used for HTML rendering. */
  renderModel: string;
  /** Combined total cost (generation + rendering) in USD. */
  totalEstimatedCostUsd: number;
}

interface EstimateOptions {
  /** AI provider — defaults to Anthropic. */
  provider?: AIProviderCode;
  /** Model ID — defaults to Claude Sonnet 4.5. */
  model?: string;
}

/**
 * Estimates the AI cost for generating a workbook at the given pace.
 *
 * Includes both content generation (exercise blocks + back matter) and
 * HTML rendering (per-block LLM rendering via Haiku).
 *
 * Pricing is looked up from the shared MODEL_PRICING table based on the
 * provider/model combo. Defaults to Anthropic Sonnet for backward compat.
 *
 * This is a rough estimate — actual usage may vary depending on prompt length
 * and output complexity.
 */
export function estimateGenerationCost(
  pace: PaceCode,
  options?: EstimateOptions,
): GenerationCostEstimate {
  const provider = options?.provider ?? DEFAULT_AI_PROVIDER;
  const model = options?.model ?? DEFAULT_AI_MODEL;

  const pricing = getModelPricing(provider, model);
  const blockCount = getBlockCount(pace);

  // Exercise block tokens (content generation)
  const blockInputTokens = blockCount * TOKENS_PER_BLOCK_INPUT;
  const blockOutputTokens = blockCount * TOKENS_PER_BLOCK_OUTPUT;

  // Back matter tokens (verb glossary + cultural notes)
  const backMatterInputTokens = BACK_MATTER_CALLS * TOKENS_PER_BACK_MATTER_INPUT;
  const backMatterOutputTokens = BACK_MATTER_CALLS * TOKENS_PER_BACK_MATTER_OUTPUT;

  const estimatedInputTokens = blockInputTokens + backMatterInputTokens;
  const estimatedOutputTokens = blockOutputTokens + backMatterOutputTokens;

  const estimatedCostUsd =
    (estimatedInputTokens * pricing.inputCostPerMillion +
      estimatedOutputTokens * pricing.outputCostPerMillion) /
    1_000_000;

  // HTML rendering cost (always uses Haiku, regardless of generation model)
  const renderPricing = getModelPricing('anthropic', DEFAULT_RENDER_MODEL);
  const renderInputTokens = Math.round(
    blockCount * RENDER_TOKENS_PER_BLOCK_INPUT * RENDER_RETRY_MULTIPLIER,
  );
  const renderOutputTokens = Math.round(
    blockCount * RENDER_TOKENS_PER_BLOCK_OUTPUT * RENDER_RETRY_MULTIPLIER,
  );
  const renderEstimatedCostUsd =
    (renderInputTokens * renderPricing.inputCostPerMillion +
      renderOutputTokens * renderPricing.outputCostPerMillion) /
    1_000_000;

  // Page counts per pace tier
  const pageCounts: Record<PaceCode, number> = {
    light: 24,
    regular: 32,
    intensive: 40,
    complete: 100,
  };

  return {
    blockCount,
    estimatedPages: pageCounts[pace],
    estimatedInputTokens,
    estimatedOutputTokens,
    estimatedCostUsd,
    provider,
    model,
    renderEstimatedCostUsd,
    renderModel: DEFAULT_RENDER_MODEL,
    totalEstimatedCostUsd: estimatedCostUsd + renderEstimatedCostUsd,
  };
}
